import React from "react";

import ChevronDown from "@uui/ChevronDown";
import FilterLines from "@uui/FilterLines";
import { shallow } from "zustand/shallow";

import { Box, Divider, Typography } from "@mui/material";

import useCalendarStore from "@app/analytics/Calendar/hooks/useCalendarStore";
import { FilterCheckbox, HeaderButton } from "@app/analytics/Calendar/styles";
import type { CalendarFilter } from "@app/analytics/Calendar/types";
import FilterCountChip from "@app/analytics/components/FilterCountChip";
import { CONFERENCING_PLATFORMS_BY_ID } from "@app/common/constants/platforms";
import { CALENDAR_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import { ELLIPSIS_SX } from "@app/common/styles";
import { ConferencingMeetingPlatform } from "@app/common/types";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import Menu from "@rui/Menu";
import Stack, { Row } from "@rui/Stack";
import { Img } from "@rui/styles";

const UpcomingEventsFilterMenu = () => {
  const { open, onClose, onClick, anchorElement } = useAnchorElement();
  const {
    calendars,
    platforms,
    toggleAllCalendars,
    toggleAllPlatforms,
    toggleCalendar,
    togglePlatform,
  } = useCalendarStore(
    (state) => ({
      calendars: state.calendars,
      platforms: state.platforms,
      toggleAllCalendars: state.toggleAllCalendars,
      toggleAllPlatforms: state.toggleAllPlatforms,
      toggleCalendar: state.toggleCalendar,
      togglePlatform: state.togglePlatform,
    }),
    shallow
  );

  const filterCount =
    (calendars.allSelected ? 0 : 1) + (platforms.allSelected ? 0 : 1);
  const hasFilters = filterCount > 0;

  const toggleCalendarFilter =
    (calendar: CalendarFilter) =>
    (event: React.SyntheticEvent, checked: boolean) => {
      toggleCalendar(calendar, checked);
    };

  const togglePlatformFilter =
    (value: ConferencingMeetingPlatform) =>
    (event: React.SyntheticEvent, checked: boolean) => {
      togglePlatform(value, checked);
    };

  return (
    <>
      <HeaderButton
        startIcon={<FilterLines />}
        endIcon={<ChevronDown />}
        onClick={onClick}
      >
        <span className="header-button__label">Filter</span>
        {hasFilters && <FilterCountChip>{filterCount}</FilterCountChip>}
      </HeaderButton>
      <Menu
        open={open}
        onClose={onClose}
        anchorEl={anchorElement}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Stack spacing={1} sx={{ p: 0.5, width: "220px" }}>
          <FilterCheckbox
            label={
              <Typography variant="bodySecondaryMed">
                Platform ({platforms.all.length})
              </Typography>
            }
            checked={platforms.allSelected}
            onChange={(e, checked) => toggleAllPlatforms(checked)}
          />
          {platforms.all.map((platform) => {
            const config = CONFERENCING_PLATFORMS_BY_ID[platform];
            return (
              <FilterCheckbox
                key={platform}
                label={
                  <Row variant="left" spacing={1}>
                    <Img
                      src={config.icon}
                      sx={{ height: "20px", width: "20px" }}
                    />
                    <Typography variant="bodySecondaryReg">
                      {config.displayName}
                    </Typography>
                  </Row>
                }
                checked={platforms.filters.includes(platform)}
                onChange={togglePlatformFilter(platform)}
              />
            );
          })}
          <Divider />
          <FilterCheckbox
            label={
              <Typography variant="bodySecondaryMed">
                Calendar ({calendars.all.length})
              </Typography>
            }
            checked={calendars.allSelected}
            onChange={(e, checked) => toggleAllCalendars(checked)}
          />
          {calendars.all.map((calendar) => (
            <FilterCheckbox
              key={`${calendar.id}-${calendar.summary}`}
              label={
                <Row variant="left" spacing={1}>
                  <Box
                    component="img"
                    src={CALENDAR_PROVIDERS_BY_ID[calendar.provider].icon}
                    sx={{ height: "20px", width: "20px" }}
                  />
                  <Typography
                    variant="bodySecondaryReg"
                    sx={ELLIPSIS_SX}
                    title={calendar.summary}
                  >
                    {calendar.summary}
                  </Typography>
                </Row>
              }
              checked={calendars.filters.includes(calendar)}
              onChange={toggleCalendarFilter(calendar)}
            />
          ))}
        </Stack>
      </Menu>
    </>
  );
};

export default UpcomingEventsFilterMenu;
